// src/components/shopping/ThriftProductDetailModal.tsx
import React, { useState, useEffect } from 'react';
import {
  X, Heart, Share2, MessageSquare, ShoppingBag, MapPin,
  Clock, Tag, Shield, User, Loader2, CheckCircle, Send,
  ChevronLeft, ChevronRight, AlertTriangle,
  Link as LinkIcon, Users, ArrowLeft, CreditCard
} from 'lucide-react';
import { db } from '../../services/firebase';
import {
  doc, setDoc, addDoc, collection, increment, serverTimestamp
} from 'firebase/firestore';

interface ThriftProductDetailModalProps {
  item: any;
  isOpen: boolean;
  onClose: () => void;
  onToggleWishlist?: (id: string, type: 'product') => void;
  isWishlisted: boolean;
  userProfile?: any;
  currentUser?: any;
  requireAuth?: (fn: () => void, message: string) => void;
  onViewProfile?: (userId: string) => void;
  onSendMessage?: (sellerId: string, sellerName: string) => void;
  onAddToCart?: (item: any) => void;
  onBuyNow?: (item: any) => void;
  cartItems?: any[];
}

export const ThriftProductDetailModal: React.FC<ThriftProductDetailModalProps> = ({
  item,
  isOpen,
  onClose,
  onToggleWishlist,
  isWishlisted,
  userProfile,
  currentUser,
  requireAuth,
  onViewProfile,
  onSendMessage,
  onAddToCart,
  onBuyNow,
  cartItems = []
}) => {
  const [isMakingOffer, setIsMakingOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [isSendingAction, setIsSendingAction] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [quickMessage, setQuickMessage] = useState('Hi, is this still available?');
  const [showSuccess, setShowSuccess] = useState(false);

  const thriftImages = item?.images?.length > 0 ? item.images : [item?.image];
  const sellerName = item?.userName ||
    item?.sellerName ||
    item?.brandName ||
    item?.businessName ||
    item?.displayName ||
    item?.storeName ||
    'Unknown Seller';
  const sellerId = item?.userId || item?.sellerId || item?.businessId || item?.storeId || item?.workspaceId;
  const isSold = item?.status === 'sold' || item?.stock === 0;
  const isInCart = cartItems.some(cartItem => cartItem.id === item?.id);

  useEffect(() => {
    if (item && isOpen) {
      setCurrentImageIndex(0);
      setIsMakingOffer(false);
      setOfferAmount('');
    }
  }, [item, isOpen]);

  if (!isOpen || !item) return null;

  const handleAddToCart = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to add items to your cart.");
      return;
    }

    if (isSold) {
      alert("This item has been sold.");
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.itemName,
      price: item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,
      sellerId: sellerId,
      sellerName: sellerName,
      sellerAvatar: item.userAvatar || item.sellerAvatar || '',
      workspaceType: 'thrift',
      type: 'thrift',
      workspaceId: sellerId,
      businessId: sellerId,
      condition: item.condition || '',
      era: item.era || '',
      styleCategory: item.styleCategory || '',
      size: item.size || '',
      color: item.color || '',
      authenticationBadge: item.authenticationBadge || false,
      isOneOfOne: true,
      originalItem: item
    };

    onAddToCart?.(cartItem);
  };

  const handleBuyNow = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to purchase this item.");
      return;
    }

    if (isSold) {
      alert("This item has been sold.");
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.itemName,
      price: item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,
      sellerId: sellerId,
      sellerName: sellerName,
      sellerAvatar: item.userAvatar || item.sellerAvatar || '',
      workspaceType: 'thrift',
      type: 'thrift',
      workspaceId: sellerId,
      businessId: sellerId,
      condition: item.condition || '',
      era: item.era || '',
      styleCategory: item.styleCategory || '',
      size: item.size || '',
      color: item.color || '',
      authenticationBadge: item.authenticationBadge || false,
      isOneOfOne: true,
      originalItem: item
    };

    onBuyNow?.(cartItem);
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/thrifts?post=${item.id}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareMenu(false);
    alert("Link copied to clipboard!");
  };

  const handleNativeShare = async () => {
    const shareUrl = `${window.location.origin}/thrifts?post=${item.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.itemName,
          text: `Check out "${item.itemName}" on Scoop Hype! 🔥`,
          url: shareUrl
        });
        setShowShareMenu(false);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      handleCopyLink();
    }
  };

  const handleViewProfile = () => {
    if (sellerId && onViewProfile) {
      onClose();
      setTimeout(() => {
        onViewProfile(sellerId);
      }, 300);
    } else {
      alert("This seller's profile could not be found.");
    }
  };

  const handleSendMessage = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to message this seller.");
      return;
    }

    if (onSendMessage && sellerId) {
      onSendMessage(sellerId, sellerName);
    } else {
      window.dispatchEvent(new CustomEvent('open-messages', {
        detail: { userId: sellerId }
      }));
    }
  };

  const handleQuickAsk = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to send a message.");
      return;
    }

    if (!sellerId) {
      alert("Seller ID not found.");
      return;
    }

    setIsSendingAction(true);

    try {
      const chatId = [currentUser.uid, sellerId].sort().join('_');
      const chatRef = doc(db, 'chats', chatId);

      setDoc(chatRef, {
        participants: [currentUser.uid, sellerId],
        updatedAt: serverTimestamp(),
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.displayName || 'User',
          [sellerId]: sellerName
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [sellerId]: item?.userAvatar || item?.sellerAvatar || ''
        },
        hidden: { [currentUser.uid]: false, [sellerId]: false },
        lastMessage: quickMessage || 'Hi, is this still available?',
        [`unreadCount.${sellerId}`]: increment(1)
      }, { merge: true });

      addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: quickMessage || 'Hi, is this still available?',
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'text',
        productName: item.itemName,
        productId: item.id,
        productImage: item.image
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSendingAction(false);
    }
  };

  const handleMakeOffer = () => {
    if (!currentUser) {
      requireAuth?.(() => {}, "Log in to make an offer.");
      return;
    }

    if (!offerAmount || isNaN(Number(offerAmount))) {
      alert("Please enter a valid offer amount.");
      return;
    }

    setIsSendingAction(true);

    try {
      const chatId = [currentUser.uid, sellerId].sort().join('_');
      const chatRef = doc(db, 'chats', chatId);

      const message = `${userProfile?.displayName || 'A user'} made an offer of R${offerAmount} for "${item.itemName}".`;

      setDoc(chatRef, {
        participants: [currentUser.uid, sellerId],
        updatedAt: serverTimestamp(),
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.displayName || 'User',
          [sellerId]: sellerName
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [sellerId]: item?.userAvatar || item?.sellerAvatar || ''
        },
        hidden: { [currentUser.uid]: false, [sellerId]: false },
        lastMessage: message,
        [`unreadCount.${sellerId}`]: increment(1)
      }, { merge: true });

      addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: message,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'offer',
        offerAmount: Number(offerAmount),
        productName: item.itemName,
        productId: item.id,
        productImage: item.image,
        offerStatus: 'pending'
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsMakingOffer(false);
        setOfferAmount('');
      }, 2000);
    } catch (error) {
      console.error("Error sending offer:", error);
      alert("Failed to send offer. Please try again.");
    } finally {
      setIsSendingAction(false);
    }
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % thriftImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + thriftImages.length) % thriftImages.length);

  return (
    <div className="fixed inset-0 z-[1000] bg-black text-white flex flex-col lg:flex-row animate-in fade-in">
      {/* Back Button */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 z-[60] p-2 bg-black/80 border border-white/20 hover:bg-white hover:text-black text-white transition-colors rounded-full backdrop-blur-md"
      >
        <ArrowLeft size={18} />
      </button>

      {/* LEFT: IMAGE */}
      <div className="w-full lg:w-[55%] h-[40vh] lg:h-full shrink-0 bg-zinc-900 border-b lg:border-b-0 lg:border-r border-white/10 relative">
        <div className="relative w-full h-full overflow-hidden">
          <img
            src={thriftImages[currentImageIndex]}
            className="w-full h-full object-contain"
            alt={item.itemName}
          />
          {isSold && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-2xl font-black text-white uppercase tracking-widest border-2 border-white px-4 py-2 rotate-[-15deg]">SOLD</span>
            </div>
          )}
          {thriftImages.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/70 text-white p-1.5 rounded-full border border-white/20"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/70 text-white p-1.5 rounded-full border border-white/20"
              >
                <ChevronRight size={16} />
              </button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {thriftImages.map((_: any, idx: number) => (
                  <div
                    key={idx}
                    className={`h-1 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-4' : 'bg-white/40 w-1.5'}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* RIGHT: DETAILS */}
      <div className="w-full lg:w-[45%] flex flex-col bg-black overflow-y-auto h-[60vh] lg:h-full">
        {/* Header - Fixed */}
        <div className="p-4 lg:p-6 border-b border-white/10 shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg lg:text-xl font-black text-white uppercase tracking-tighter truncate">
                {item.itemName}
              </h2>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <p className="text-lg text-white font-mono font-black">R {item.price}</p>
                {item.condition && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/10 text-white text-[9px] font-black uppercase rounded-full border border-white/20">
                    <Shield size={10} /> {item.condition}
                  </span>
                )}
                {isSold && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-[9px] font-black uppercase rounded-full border border-red-500/30">
                    SOLD
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0 ml-3">
              <button
                onClick={() => onToggleWishlist?.(item.id, 'product')}
                className="p-2 bg-white/10 border border-white/20 hover:bg-white hover:text-black transition-colors text-white rounded-lg"
              >
                <Heart
                  size={16}
                  fill={isWishlisted ? "currentColor" : "none"}
                  className={isWishlisted ? "text-white" : "text-white/60"}
                />
              </button>
              <button
                onClick={() => setShowShareMenu(true)}
                className="p-2 bg-white/10 border border-white/20 hover:bg-white hover:text-black transition-colors text-white rounded-lg"
              >
                <Share2 size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4 scrollbar-hide">
          {/* Meta attributes - Compact pills */}
          <div className="flex flex-wrap gap-1.5">
            {item.era && (
              <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-full text-[9px] font-bold text-white/60">
                {item.era}
              </span>
            )}
            {item.size && (
              <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-full text-[9px] font-bold text-white/60">
                Size: {item.size}
              </span>
            )}
            {item.color && (
              <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-full text-[9px] font-bold text-white/60">
                {item.color}
              </span>
            )}
            {item.brandName && (
              <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-full text-[9px] font-bold text-white/60">
                {item.brandName}
              </span>
            )}
            {item.authenticationBadge && (
              <span className="px-2 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-[9px] font-bold text-green-400 flex items-center gap-1">
                <Shield size={10} /> Verified
              </span>
            )}
            <span className="px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full text-[9px] font-bold text-amber-400 flex items-center gap-1">
              <Tag size={10} /> 1 of 1
            </span>
          </div>

          {/* Description */}
          {item.description && (
            <p className="text-xs text-white/60 leading-relaxed border-l-2 border-white/10 pl-3">
              {item.description}
            </p>
          )}

          {/* 🟢 ACTION BUTTONS - Side by side, compact */}
          {!isSold && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <button
                  onClick={handleAddToCart}
                  disabled={isInCart}
                  className={`flex-1 py-2.5 font-black uppercase tracking-widest text-[10px] rounded-lg flex items-center justify-center gap-2 transition-all ${
                    isInCart
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
                      : 'bg-white text-black hover:bg-zinc-200'
                  }`}
                >
                  {isInCart ? (
                    <>
                      <CheckCircle size={14} /> In Cart
                    </>
                  ) : (
                    <>
                      <ShoppingBag size={14} /> Add to Cart
                    </>
                  )}
                </button>

                <button
                  onClick={handleBuyNow}
                  className="flex-1 py-2.5 bg-white/10 border-2 border-white/30 text-white font-black uppercase tracking-widest text-[10px] rounded-lg hover:bg-white/20 flex items-center justify-center gap-2 transition-all"
                >
                  <CreditCard size={14} /> Buy Now
                </button>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-black text-white/30 text-[8px] font-black uppercase tracking-widest">or</span>
                </div>
              </div>

              {/* Offer & Message - Side by side */}
              <div className="flex gap-2">
                <button
                  onClick={() => setIsMakingOffer(true)}
                  className="flex-1 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-black uppercase tracking-widest text-[10px] rounded-lg hover:bg-amber-500/20 flex items-center justify-center gap-2 transition-all"
                >
                  <Tag size={14} /> Make Offer
                </button>
                <button
                  onClick={handleSendMessage}
                  className="flex-1 py-2 bg-white/5 border border-white/20 text-white font-black uppercase tracking-widest text-[10px] rounded-lg hover:bg-white/10 flex items-center justify-center gap-2 transition-all"
                >
                  <MessageSquare size={14} /> Message
                </button>
              </div>
            </div>
          )}

          {/* Offer Input */}
          {isMakingOffer && !isSold && (
            <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg animate-in slide-in-from-top-2">
              <p className="text-[9px] text-amber-400 font-mono uppercase tracking-widest mb-2">
                Your offer (Current: R{item.price})
              </p>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 font-black text-sm">R</span>
                  <input
                    type="number"
                    autoFocus
                    value={offerAmount}
                    onChange={(e) => setOfferAmount(e.target.value)}
                    placeholder="Amount"
                    className="w-full bg-black border border-amber-500/30 rounded-lg py-2 pl-7 pr-3 text-amber-400 text-sm outline-none focus:border-amber-500"
                  />
                </div>
                <button
                  onClick={handleMakeOffer}
                  disabled={!offerAmount || isSendingAction}
                  className="px-4 py-2 bg-amber-500 text-black font-black uppercase tracking-widest text-[9px] rounded-lg disabled:opacity-50 hover:bg-amber-400 transition-colors"
                >
                  {isSendingAction ? <Loader2 size={14} className="animate-spin" /> : 'Send'}
                </button>
                <button
                  onClick={() => setIsMakingOffer(false)}
                  className="p-2 bg-white/10 border border-white/20 text-white/50 hover:text-white rounded-lg"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Quick Ask - Compact */}
          {!isMakingOffer && !isSold && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-2.5 flex items-center gap-2">
              <input
                type="text"
                value={quickMessage}
                onChange={(e) => setQuickMessage(e.target.value)}
                className="flex-1 bg-transparent text-white/60 text-xs font-mono outline-none placeholder:text-white/20"
                placeholder="Ask seller..."
              />
              {showSuccess ? (
                <CheckCircle size={16} className="text-green-500 shrink-0" />
              ) : (
                <button
                  onClick={handleQuickAsk}
                  disabled={isSendingAction || !quickMessage.trim()}
                  className="px-3 py-1.5 bg-white text-black text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-1 shrink-0"
                >
                  {isSendingAction ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send
                </button>
              )}
            </div>
          )}

          {/* Seller info - Compact */}
          <div>
            <h3 className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-2 border-b border-white/10 pb-1.5">
              Seller
            </h3>
            <button
              onClick={handleViewProfile}
              className="flex items-center gap-2 hover:bg-white/5 p-1.5 rounded-lg transition-colors w-full text-left"
            >
              <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 overflow-hidden flex items-center justify-center shrink-0">
                {item.userAvatar || item.sellerAvatar ? (
                  <img
                    src={item.userAvatar || item.sellerAvatar}
                    className="w-full h-full object-cover"
                    alt={sellerName}
                  />
                ) : (
                  <User size={14} className="text-white/50" />
                )}
              </div>
              <div>
                <p className="text-white font-black text-xs">{sellerName}</p>
                <p className="text-white/40 text-[8px] uppercase tracking-widest">View Profile →</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div
          className="fixed inset-0 z-[1200] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setShowShareMenu(false)}
        >
          <div
            className="bg-black border border-white/10 rounded-2xl p-6 w-full max-w-xs text-center shadow-2xl animate-in zoom-in-95"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Share2 className="text-white" size={24} />
            </div>
            <h3 className="text-white font-black uppercase text-sm mb-1">Share Item</h3>
            <p className="text-white/50 text-[10px] mb-6 uppercase font-bold tracking-tighter">
              "{item.itemName}"
            </p>

            <div className="space-y-2">
              <button
                onClick={handleNativeShare}
                className="w-full py-3 bg-white text-black rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <Users size={14} /> Share
              </button>

              <button
                onClick={handleCopyLink}
                className="w-full py-3 bg-white/10 text-white rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <LinkIcon size={14} /> Copy Link
              </button>
            </div>

            <button
              onClick={() => setShowShareMenu(false)}
              className="mt-4 text-white/50 uppercase font-black text-[9px] hover:text-white transition-colors tracking-widest"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThriftProductDetailModal;